import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import ShareModal from '../components/ShareModal';
import { useToast } from '../components/Toast';

const ShareContext = createContext(null);

/**
 * Centralizes the "share a song" UX. Always opens the dedicated ShareModal
 * with a copy-link button — intentionally skipping `navigator.share` so the
 * sharing surface is consistent across devices and on-brand.
 *
 * Callers just do `share(song)` via the `useShare` hook.
 */
export const ShareProvider = ({ children }) => {
  const [activeSong, setActiveSong] = useState(null);
  const { showToast } = useToast();

  const close = useCallback(() => setActiveSong(null), []);

  const share = useCallback((song) => {
    if (!song?.id) return;
    setActiveSong(song);
  }, []);

  const handleCopyResult = useCallback(
    (result) => {
      if (result === 'copied') {
        showToast('Link copied', { variant: 'success' });
      } else if (result === 'failed') {
        showToast("Couldn't copy link", { variant: 'error' });
      }
    },
    [showToast]
  );

  const value = useMemo(() => ({ share }), [share]);

  return (
    <ShareContext.Provider value={value}>
      {children}
      <ShareModal
        show={!!activeSong}
        song={activeSong}
        onClose={close}
        onCopyResult={handleCopyResult}
      />
    </ShareContext.Provider>
  );
};

export const useShare = () => {
  const ctx = useContext(ShareContext);
  if (!ctx) {
    // Safe no-op when provider is missing; avoids forcing every test/story
    // file to wrap its trees.
    return { share: () => {} };
  }
  return ctx;
};

export default ShareContext;
